import { inject, Injectable } from '@angular/core';
import { IAccount, IAccountPreferences, ICompany } from '@okcargo-apps/shared-domain-ionic';
import {
  CollaboratorRoutesPreferencesFieldNames,
  FilterCombinator,
  FilterComparator,
  FindCollaboratorQuestionairQuery,
  FindContactQuery,
  OrderType,
  SearchCollaboratorRoutesPreferencesQuery,
} from '@okcargo/query-processor';
import { CollaboratorPreferencesQueryService } from '../collaborator-preferences';
import { CompanyQueryService } from '../company';
import { ContactQueryService } from '../contact';
import { AccountDataQueryService } from './account-data-query.service';
import {
  getICompany,
  transformAccountResponseToIAccount,
  transformFromSitesToICostCenters,
  transformToIAccountPreferences,
} from './account-mapper';

@Injectable({
  providedIn: 'root',
})
export class AccountQueryService {
  private readonly collaboratorPreferencesQueryService = inject(CollaboratorPreferencesQueryService);
  private readonly accountDataQueryService = inject(AccountDataQueryService);
  private readonly companyQueryService = inject(CompanyQueryService);
  private readonly contactQueryService = inject(ContactQueryService);

  public async getIdentity(account: IAccount): Promise<IAccount> {
    // parallel execution with Promise.all
    const [accountResponse, companyResponse, sitesResponse] = await Promise.all([
      this.accountDataQueryService.findAccount(),
      this.companyQueryService.findCompany(),
      this.companyQueryService.findSites(),
    ]);
    return transformAccountResponseToIAccount(accountResponse, companyResponse, sitesResponse, account);
  }

  public async findAccountPreferences(
    companyId: any,
    userEmail: string,
    accountPreferences: IAccountPreferences,
  ): Promise<IAccountPreferences> {
    const findCollaboratorQuestionairQuery = {
      carrierId: companyId,
    } as FindCollaboratorQuestionairQuery;

    const searchCollaboratorRoutesPreferencesQuery = {
      criteriaPayload: {
        filters: [
          {
            field: CollaboratorRoutesPreferencesFieldNames.CARRIER_ID,
            comparator: FilterComparator.EQ,
            value: companyId,
            combinator: FilterCombinator.NONE,
            group: null,
          },
        ],
        order: [
          {
            field: CollaboratorRoutesPreferencesFieldNames.ID,
            type: OrderType.ASC,
          },
        ],
        pageIndex: 0,
        pageSize: 1000,
      },
    } as unknown as SearchCollaboratorRoutesPreferencesQuery;

    const [accountPreferencesBaseResponse, accountPreferencesRoutesResponse, companyResponse, contactResponse] =
      await Promise.all([
        this.collaboratorPreferencesQueryService.findCollaboratorQuestionair(findCollaboratorQuestionairQuery),
        this.collaboratorPreferencesQueryService.searchCollaboratorRoutesPreferences(
          searchCollaboratorRoutesPreferencesQuery,
        ),
        this.companyQueryService.findCompany(),
        this.contactQueryService.findContact({} as FindContactQuery),
      ]);

    transformToIAccountPreferences(
      accountPreferencesBaseResponse,
      accountPreferencesRoutesResponse,
      companyResponse,
      contactResponse,
      accountPreferences,
      userEmail,
    );

    return accountPreferences;
  }

  public async getCompanyData(): Promise<ICompany> {
    const [companyResponse, sitesResponse] = await Promise.all([
      this.companyQueryService.findCompany(),
      this.companyQueryService.findSites(),
    ]);

    // Reutilizamos tus funciones puras existentes en el mismo archivo
    const company = getICompany(companyResponse);
    company.costCenters = transformFromSitesToICostCenters(sitesResponse);

    return company;
  }
}
